"use client";

import { Contract, formatPLN, contractKey } from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const MONTHS = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
];

interface ContractsTableProps {
  contracts: Contract[];
  selected: Set<string>;
  onToggle: (contractNumber: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onCalculate: (year: number, month: number) => void;
  loading: boolean;
  year: number;
  month: number;
  onYearChange: (year: number) => void;
  onMonthChange: (month: number) => void;
}

export function ContractsTable({
  contracts,
  selected,
  onToggle,
  onSelectAll,
  onDeselectAll,
  onCalculate,
  loading,
  year,
  month,
  onYearChange,
  onMonthChange,
}: ContractsTableProps) {
  const allSelected = contracts.length > 0 && selected.size === contracts.length;
  const currentYear = new Date().getFullYear();

  return (
    <div className="glass rounded-2xl p-6 glow-blue-hover transition-smooth">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold text-blue-100">
            Umowy ({contracts.length})
          </h2>
          <span className="text-sm text-muted-foreground">
            Zaznaczono: {selected.size}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onSelectAll}
            className="px-4 py-2 text-sm rounded-lg glass glass-hover text-blue-300 hover:text-white transition-smooth"
          >
            Zaznacz wszystkie
          </button>
          <button
            onClick={onDeselectAll}
            className="px-4 py-2 text-sm rounded-lg glass glass-hover text-blue-300 hover:text-white transition-smooth"
          >
            Odznacz wszystkie
          </button>

          {/* Year select */}
          <select
            value={year}
            onChange={(e) => onYearChange(Number(e.target.value))}
            className="
              px-3 py-2.5 rounded-xl text-sm font-medium
              glass border border-blue-500/20 text-blue-100
              bg-transparent appearance-none cursor-pointer
              focus:outline-none focus:border-blue-500/50
              transition-smooth
            "
          >
            <option value={currentYear} className="bg-[#111827]">{currentYear}</option>
            <option value={currentYear - 1} className="bg-[#111827]">{currentYear - 1}</option>
          </select>

          {/* Month select */}
          <select
            value={month}
            onChange={(e) => onMonthChange(Number(e.target.value))}
            className="
              px-3 py-2.5 rounded-xl text-sm font-medium
              glass border border-blue-500/20 text-blue-100
              bg-transparent appearance-none cursor-pointer
              focus:outline-none focus:border-blue-500/50
              transition-smooth
            "
          >
            {MONTHS.map((name, i) => (
              <option key={i + 1} value={i + 1} className="bg-[#111827]">
                {name}
              </option>
            ))}
          </select>

          <button
            onClick={() => onCalculate(year, month)}
            disabled={selected.size === 0 || loading}
            className="
              px-6 py-2.5 rounded-xl font-semibold text-sm
              bg-gradient-to-r from-blue-600 to-blue-500
              text-white glow-blue
              hover:from-blue-500 hover:to-blue-400
              hover:scale-105 active:scale-95
              disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100
              transition-smooth
              flex items-center gap-2
            "
          >
            {loading && <span className="spinner !w-4 !h-4" />}
            Oblicz
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl overflow-hidden border border-blue-500/10">
        <Table>
          <TableHeader>
            <TableRow className="border-blue-500/10 hover:bg-transparent">
              <TableHead className="w-12">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={() =>
                    allSelected ? onDeselectAll() : onSelectAll()
                  }
                  className="border-blue-500/30 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                />
              </TableHead>
              <TableHead className="text-blue-300">Nr rejestracyjny</TableHead>
              <TableHead className="text-blue-300">Nr umowy</TableHead>
              <TableHead className="text-blue-300">Data zawarcia</TableHead>
              <TableHead className="text-blue-300 text-right">
                Wartość umowy
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contracts.map((contract) => {
              const key = contractKey(contract);
              return (
              <TableRow
                key={key}
                className="border-blue-500/10 hover:bg-blue-500/5 transition-smooth cursor-pointer"
                onClick={() => onToggle(key)}
              >
                <TableCell>
                  <Checkbox
                    checked={selected.has(key)}
                    onCheckedChange={() => onToggle(key)}
                    className="border-blue-500/30 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                  />
                </TableCell>
                <TableCell className="font-mono text-blue-100">
                  {contract.registration_number}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {contract.contract_number}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {contract.contract_date || "-"}
                </TableCell>
                <TableCell className="text-right font-mono text-blue-100">
                  {contract.contract_value
                    ? formatPLN(contract.contract_value)
                    : "-"}
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
